import { Controller } from "@hotwired/stimulus"

// Contrôleur pour la sélection de joueurs (existant ou nouveau)
// Utilisé dans le formulaire d'inscription d'équipe par un JAP
export default class extends Controller {
  static targets = [
    "existingTab", "newTab",
    "existingPanel", "newPanel",
    "searchInput", "results", "playerId", "selectedPlayer",
    "newEmail", "newNom", "newPrenom", "newClassement", "newLicence"
  ]

  static values = {
    editModalUrlTemplate: String
  }

  connect() {
    this.showExisting()
    this.searchTimeout = null
    this.selectedPlayerData = null
    this.currentQuery = ''
    this.currentPage = 1
    this.hasMore = false
    this.isLoading = false
    this.allPlayers = []
  }

  showExisting() {
    this.existingTabTarget.classList.add("border-blue-500", "text-blue-600")
    this.existingTabTarget.classList.remove("border-transparent", "text-gray-500")
    this.newTabTarget.classList.remove("border-blue-500", "text-blue-600")
    this.newTabTarget.classList.add("border-transparent", "text-gray-500")

    this.existingPanelTarget.classList.remove("hidden")
    this.newPanelTarget.classList.add("hidden")

    this.clearNewPlayerFields()
  }

  showNew() {
    this.newTabTarget.classList.add("border-blue-500", "text-blue-600")
    this.newTabTarget.classList.remove("border-transparent", "text-gray-500")
    this.existingTabTarget.classList.remove("border-blue-500", "text-blue-600")
    this.existingTabTarget.classList.add("border-transparent", "text-gray-500")

    this.newPanelTarget.classList.remove("hidden")
    this.existingPanelTarget.classList.add("hidden")

    this.clearExistingPlayerSelection()
  }

  search() {
    const query = this.searchInputTarget.value.trim()

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    if (query.length < 2) {
      this.resultsTarget.classList.add("hidden")
      this.currentQuery = ''
      this.allPlayers = []
      return
    }

    // Reset pagination for new search
    if (query !== this.currentQuery) {
      this.currentPage = 1
      this.allPlayers = []
    }

    this.searchTimeout = setTimeout(() => {
      this.performSearch(query, 1)
    }, 300)
  }

  async performSearch(query, page = 1) {
    const url = this.searchInputTarget.dataset.searchUrl
    if (!url || this.isLoading) return

    this.isLoading = true
    this.currentQuery = query

    try {
      const response = await fetch(`${url}?q=${encodeURIComponent(query)}&page=${page}`, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (!response.ok) throw new Error("Search failed")

      const data = await response.json()

      if (page === 1) {
        this.allPlayers = data.players
      } else {
        this.allPlayers = [...this.allPlayers, ...data.players]
      }

      this.hasMore = data.has_more
      this.currentPage = data.page

      this.displayResults(this.allPlayers, data.total)
    } catch (error) {
      console.error("Search error:", error)
    } finally {
      this.isLoading = false
    }
  }

  loadMore(event) {
    event.preventDefault()
    if (this.hasMore && !this.isLoading) {
      this.performSearch(this.currentQuery, this.currentPage + 1)
    }
  }

  displayResults(players, total = 0) {
    if (players.length === 0) {
      this.resultsTarget.innerHTML = `
        <div class="p-4 text-sm text-gray-500 text-center">
          <p>Aucun joueur trouvé pour "<strong>${this.currentQuery}</strong>"</p>
          <p class="mt-1">Essayez "Nouveau joueur" pour créer un compte.</p>
        </div>
      `
      this.resultsTarget.classList.remove("hidden")
      return
    }

    const playersHtml = players.map(player => {
      const hasLicence = player.licence_fft && player.licence_fft.length > 0
      const licenceBadge = hasLicence
        ? `<span class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">Licence OK</span>`
        : `<span class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">Sans licence</span>`

      return `
        <div class="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
             data-action="click->player-selector#selectPlayer"
             data-player-id="${player.id}"
             data-player-nom="${player.nom}"
             data-player-prenom="${player.prenom}"
             data-player-email="${player.email}"
             data-player-classement="${player.classement_fft || ''}"
             data-player-licence="${player.licence_fft || ''}"
             data-player-display="${player.display}">
          <div class="flex items-center justify-between">
            <div class="font-medium text-gray-900">${player.nom} ${player.prenom}</div>
            ${licenceBadge}
          </div>
          <div class="text-sm text-gray-500">${player.email}</div>
          <div class="text-xs text-gray-400">
            Classement: ${player.classement_fft || 'NC'} | Licence: ${player.licence_fft || '-'}
          </div>
        </div>
      `
    }).join('')

    const countInfo = total > 0
      ? `<div class="px-3 py-2 bg-gray-50 text-xs text-gray-500 border-b border-gray-200">
           ${players.length} sur ${total} résultat${total > 1 ? 's' : ''}
         </div>`
      : ''

    const loadMoreBtn = this.hasMore
      ? `<button type="button"
                 class="w-full p-3 text-center text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors border-t border-gray-200"
                 data-action="click->player-selector#loadMore">
           <span class="inline-flex items-center gap-2">
             <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
             </svg>
             Afficher plus de résultats
           </span>
         </button>`
      : ''

    this.resultsTarget.innerHTML = countInfo + playersHtml + loadMoreBtn
    this.resultsTarget.classList.remove("hidden")
  }

  selectPlayer(event) {
    const el = event.currentTarget
    const playerId = el.dataset.playerId
    const playerNom = el.dataset.playerNom
    const playerPrenom = el.dataset.playerPrenom
    const playerEmail = el.dataset.playerEmail
    const playerClassement = el.dataset.playerClassement
    const playerLicence = el.dataset.playerLicence

    this.selectedPlayerData = {
      id: playerId,
      nom: playerNom,
      prenom: playerPrenom,
      email: playerEmail,
      classement: playerClassement,
      licence: playerLicence
    }

    this.playerIdTarget.value = playerId

    const hasLicence = playerLicence && playerLicence.length > 0
    const borderClass = hasLicence ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'
    const statusBadge = hasLicence
      ? `<span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">Licence OK</span>`
      : `<span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">Licence manquante</span>`

    const editModalUrl = this.hasEditModalUrlTemplateValue
      ? this.editModalUrlTemplateValue.replace(':id', playerId)
      : null

    const editButton = editModalUrl && !hasLicence
      ? `<button type="button"
                 class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
                 data-action="click->player-selector#openEditModal"
                 data-edit-url="${editModalUrl}"
                 data-player-id="${playerId}">
           <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
             <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
           </svg>
           Ajouter licence
         </button>`
      : ''

    this.selectedPlayerTarget.innerHTML = `
      <div class="p-4 rounded-xl border-2 ${borderClass}">
        <div class="flex items-start justify-between gap-3">
          <div class="flex-1">
            <div class="flex items-center gap-2 mb-1">
              ${statusBadge}
            </div>
            <p class="font-semibold text-gray-900 text-lg">${playerNom} ${playerPrenom}</p>
            <p class="text-sm text-gray-600">${playerEmail}</p>
            <div class="flex items-center gap-3 mt-2 text-sm">
              ${playerClassement ? `<span class="font-medium text-blue-600">P${playerClassement}</span>` : '<span class="text-gray-400">NC</span>'}
              ${hasLicence ? `<span class="text-gray-500">Licence: ${playerLicence}</span>` : ''}
            </div>
          </div>
          <button type="button"
                  class="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  data-action="click->player-selector#clearSelection">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        ${editButton ? `<div class="mt-3 pt-3 border-t border-gray-200 flex items-center gap-2">${editButton}</div>` : ''}
      </div>
    `
    this.selectedPlayerTarget.classList.remove("hidden")

    this.resultsTarget.classList.add("hidden")
    this.searchInputTarget.value = ""
    this.searchInputTarget.disabled = true
  }

  async openEditModal(event) {
    event.preventDefault()
    const url = event.currentTarget.dataset.editUrl
    const playerId = event.currentTarget.dataset.playerId

    let modal = document.getElementById('player-edit-modal')
    if (!modal) {
      modal = this.createModal()
      document.body.appendChild(modal)
    }

    const modalContent = modal.querySelector('[data-modal-target="content"]')
    const modalContainer = modal.querySelector('[data-modal-target="container"]')

    modalContainer.classList.remove('hidden')
    document.body.classList.add('overflow-hidden')
    modalContent.innerHTML = `
      <div class="flex items-center justify-center p-8">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span class="ml-3 text-gray-600">Chargement...</span>
      </div>
    `

    try {
      const response = await fetch(url, {
        headers: { "Accept": "text/html" }
      })
      const html = await response.text()

      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')
      const frameContent = doc.querySelector('turbo-frame')?.innerHTML || html

      modalContent.innerHTML = `
        <div class="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
          <div class="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
            <div class="flex items-center justify-between">
              <h3 class="text-lg font-bold text-white">Modifier le joueur</h3>
              <button type="button"
                      data-action="click->modal#close"
                      class="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>
          <div class="p-6" id="modal-form-content">
            ${frameContent}
          </div>
        </div>
      `

      const form = modalContent.querySelector('form')
      if (form) {
        form.addEventListener('submit', async (e) => {
          e.preventDefault()
          await this.handleEditFormSubmit(form, playerId, modalContainer)
        })
      }
    } catch (error) {
      console.error('Error loading edit modal:', error)
      modalContent.innerHTML = `
        <div class="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-6">
          <p class="text-red-600">Erreur lors du chargement du formulaire.</p>
          <button type="button"
                  data-action="click->modal#close"
                  class="mt-4 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
            Fermer
          </button>
        </div>
      `
    }
  }

  async handleEditFormSubmit(form, playerId, modalContainer) {
    const formData = new FormData(form)
    const submitBtn = form.querySelector('input[type="submit"], button[type="submit"]')
    const originalText = submitBtn?.textContent || submitBtn?.value

    if (submitBtn) {
      submitBtn.disabled = true
      if (submitBtn.tagName === 'INPUT') {
        submitBtn.value = 'Enregistrement...'
      } else {
        submitBtn.textContent = 'Enregistrement...'
      }
    }

    try {
      const response = await fetch(form.action, {
        method: form.method || 'PATCH',
        body: formData,
        headers: {
          "Accept": "text/html",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        }
      })

      if (response.ok) {
        modalContainer.classList.add('hidden')
        document.body.classList.remove('overflow-hidden')
        await this.refreshPlayerData(playerId)
        this.showNotification('Joueur mis à jour avec succès', 'success')
      } else {
        const html = await response.text()
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, 'text/html')
        const errors = doc.querySelector('.bg-red-50')

        if (errors) {
          const errorContainer = form.querySelector('.error-container') || form.insertAdjacentElement('afterbegin', document.createElement('div'))
          errorContainer.className = 'error-container'
          errorContainer.innerHTML = errors.outerHTML
        }

        if (submitBtn) {
          submitBtn.disabled = false
          if (submitBtn.tagName === 'INPUT') {
            submitBtn.value = originalText
          } else {
            submitBtn.textContent = originalText
          }
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error)
      this.showNotification('Erreur lors de la mise à jour', 'error')
    }
  }

  async refreshPlayerData(playerId) {
    const searchUrl = this.searchInputTarget.dataset.searchUrl
    if (!searchUrl || !this.selectedPlayerData) return

    try {
      const response = await fetch(`${searchUrl}?q=${this.selectedPlayerData.email}`, {
        headers: { "Accept": "application/json" }
      })

      if (response.ok) {
        const data = await response.json()
        const updatedPlayer = data.players?.find(p => p.id.toString() === playerId.toString())

        if (updatedPlayer) {
          this.selectedPlayerData.licence = updatedPlayer.licence_fft
          this.selectedPlayerData.classement = updatedPlayer.classement_fft

          const fakeEvent = {
            currentTarget: {
              dataset: {
                playerId: updatedPlayer.id,
                playerNom: updatedPlayer.nom,
                playerPrenom: updatedPlayer.prenom,
                playerEmail: updatedPlayer.email,
                playerClassement: updatedPlayer.classement_fft || '',
                playerLicence: updatedPlayer.licence_fft || ''
              }
            }
          }
          this.selectPlayer(fakeEvent)
        }
      }
    } catch (error) {
      console.error('Error refreshing player data:', error)
    }
  }

  createModal() {
    const modal = document.createElement('div')
    modal.id = 'player-edit-modal'
    modal.setAttribute('data-controller', 'modal')
    modal.innerHTML = `
      <div data-modal-target="container" class="hidden fixed inset-0 z-50">
        <div data-modal-target="backdrop"
             data-action="click->modal#closeOnBackdrop"
             class="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
        <div class="relative flex items-center justify-center min-h-screen p-4">
          <div data-modal-target="content" class="relative z-10">
          </div>
        </div>
      </div>
    `
    return modal
  }

  showNotification(message, type) {
    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-[60] ${
      type === 'success' ? 'bg-green-500' : 'bg-red-500'
    } text-white font-medium transform transition-all duration-300`
    notification.textContent = message

    document.body.appendChild(notification)

    setTimeout(() => {
      notification.classList.add('opacity-0')
      setTimeout(() => notification.remove(), 300)
    }, 3000)
  }

  clearSelection() {
    this.playerIdTarget.value = ""
    this.selectedPlayerTarget.classList.add("hidden")
    this.selectedPlayerTarget.innerHTML = ""
    this.searchInputTarget.disabled = false
    this.searchInputTarget.focus()
    this.selectedPlayerData = null
  }

  clearExistingPlayerSelection() {
    if (this.hasPlayerIdTarget) this.playerIdTarget.value = ""
    if (this.hasSelectedPlayerTarget) {
      this.selectedPlayerTarget.classList.add("hidden")
      this.selectedPlayerTarget.innerHTML = ""
    }
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = ""
      this.searchInputTarget.disabled = false
    }
    if (this.hasResultsTarget) this.resultsTarget.classList.add("hidden")
    this.selectedPlayerData = null
    this.allPlayers = []
    this.currentQuery = ''
    this.currentPage = 1
  }

  clearNewPlayerFields() {
    if (this.hasNewEmailTarget) this.newEmailTarget.value = ""
    if (this.hasNewNomTarget) this.newNomTarget.value = ""
    if (this.hasNewPrenomTarget) this.newPrenomTarget.value = ""
    if (this.hasNewClassementTarget) this.newClassementTarget.value = ""
    if (this.hasNewLicenceTarget) this.newLicenceTarget.value = ""
  }
}
